import {
    GetAllClustersQuery,
    In_Queue_Cluster_Model_Set_Statuses_Enum as ClusterModelSetStatus,
    In_Queue_Cluster_Statuses_Enum as ClusterStatus
} from "generated/graphql";
import { isKnownRegionType, RegionType } from "types/regionType";

// Simply rename the autogenerated enum.
export {
    In_Queue_Cluster_Model_Set_Statuses_Enum as ClusterModelSetStatus,
    In_Queue_Cluster_Statuses_Enum as ClusterStatus
} from "generated/graphql";

export type ScenarioStage = "PREVIEW" | "DECISION_POINT";

export type ClusterSlug = string;

export interface ClusterModelSet {
    // clusterModelSetId: ID of the in_queue_cluster_model_set row, i.e. join between in_queue_clusters and a model_set
    clusterModelSetId: string;
    // modelSetId: ID generated by hashing a model set's contents, i.e. the actual ID generated by models_cleaned in the pipeline
    modelSetId: string;
    name: string;
    status: ClusterModelSetStatus;
    scenarioStage: ScenarioStage;
    // When Nira attached the model set, not the ISO's own posting date.
    createdAt: Date;
}

export type ClusterModelSetAt<
    S extends ClusterModelSetStatus,
    T extends ScenarioStage
> = Omit<ClusterModelSet, "status" | "scenarioStage"> & {
    status: S;
    scenarioStage: T;
};

export interface ModelSets<T extends ScenarioStage> {
    currentModelSet: ClusterModelSetAt<ClusterModelSetStatus.Current, T> | null;
    supersededModelSets: ClusterModelSetAt<
        ClusterModelSetStatus.Superseded,
        T
    >[];
}

export interface Cluster {
    id: string;
    region: RegionType;
    studyCycle: StudyCycle;
    studyGroup: StudyGroup;
    studyPhase: StudyPhase;
    slug: ClusterSlug;
    status: ClusterStatus;
    previewModelSets: ModelSets<"PREVIEW">;
    decisionPointModelSets: ModelSets<"DECISION_POINT">;
    allModelSets: ReadonlyArray<ClusterModelSet>;
}

export interface ClusterWithLabels extends Cluster {
    cycleAndGroupLabel: string;
    phaseLabel: string;
}

const castRegion = (region: string): RegionType | null => {
    if (isKnownRegionType(region)) {
        return region;
    }
    return null;
};

export enum StudyCycle {
    MISO_2020 = "DPP-2020-Cycle",
    MISO_2021 = "DPP-2021-Cycle",
    MISO_2022 = "DPP-2022-Cycle",
    MISO_2023 = "DPP-2023-Cycle",
    MISO_2025 = "DPP-2025-Cycle",
    PJM_TC1 = "Transition Cycle 1",
    PJM_TC2 = "Transition Cycle 2",
    PJM_C1 = "Cycle 1",
    PJM_Fast_Lane = "Fast Lane",
    PJM_Serial = "Serial",
    SPP_2022 = "DISIS_2022",
    SPP_2023 = "DISIS_2023",
    SPP_2024 = "DISIS_2024",
    NYISO_CY_2023 = "CY_2023",
    NYISO_CY_2024 = "CY_2024"
}
export const isKnownStudyCycleType = (type: string): type is StudyCycle => {
    return Object.values(StudyCycle).includes(type as StudyCycle);
};

const castStudyCycle = (studyCycle: string): StudyCycle | null => {
    if (isKnownStudyCycleType(studyCycle)) {
        return studyCycle;
    }
    return null;
};

export enum StudyGroup {
    MISO_SOUTH = "South",
    MISO_CENTRAL = "Central",
    MISO_WEST = "West",
    MISO_ATC = "Atc",
    MISO_ITC = "Itc",
    // PJM and SPP don't have study groups
    DEFAULT_GROUP = "default_group"
}
export const isKnownStudyGroupType = (type: string): type is StudyGroup => {
    return Object.values(StudyGroup).includes(type as StudyGroup);
};

const castStudyGroup = (studyGroup: string): StudyGroup | null => {
    if (isKnownStudyGroupType(studyGroup)) {
        return studyGroup;
    }
    return null;
};

const castStudyPhase = (studyPhase: string): StudyPhase | null => {
    if (isKnownStudyPhaseType(studyPhase)) {
        return studyPhase;
    }
    return null;
};

export enum StudyPhase {
    RESUBMISSION = "resubmission",
    ONE_RESTUDY = "one_restudy",
    TWO_RESTUDY = "two_restudy",
    THREE_RESTUDY = "three_restudy",
    PRESCREEN = "pre_screen",
    PHASE_ONE = "one",
    PHASE_TWO = "two",
    PHASE_THREE = "three"
}

export const isKnownStudyPhaseType = (type: string): type is StudyPhase => {
    return Object.values(StudyPhase).includes(type as StudyPhase);
};

const isKnownClusterStatusType = (status: string): status is ClusterStatus => {
    return Object.values(ClusterStatus).includes(status as ClusterStatus);
};

export const castToClusterStatus = (
    status: string | null
): ClusterStatus | null => {
    if (status === null || status === undefined) {
        return null;
    }
    if (isKnownClusterStatusType(status)) {
        return status;
    }
    console.warn(`Invalid cluster status: ${status}`);
    return null;
};

const getClusterModelSets = <
    S extends ClusterModelSetStatus,
    T extends ScenarioStage
>(
    modelSets: ReadonlyArray<ClusterModelSet>,
    status: S,
    scenarioStage: T
): ClusterModelSetAt<S, T>[] => {
    return modelSets.filter(
        (ms): ms is ClusterModelSetAt<S, T> =>
            ms.status === status && ms.scenarioStage === scenarioStage
    );
};

const findClusterModelSet = <
    S extends ClusterModelSetStatus,
    T extends ScenarioStage
>(
    modelSets: ReadonlyArray<ClusterModelSet>,
    status: S,
    scenarioStage: T
): ClusterModelSetAt<S, T> | null => {
    return getClusterModelSets(modelSets, status, scenarioStage)[0] ?? null;
};

const buildModelSets = <S extends ScenarioStage>(
    modelSets: ReadonlyArray<ClusterModelSet>,
    scenarioStage: S
): ModelSets<S> => {
    return {
        currentModelSet: findClusterModelSet(
            modelSets,
            ClusterModelSetStatus.Current,
            scenarioStage
        ),
        supersededModelSets: getClusterModelSets(
            modelSets,
            ClusterModelSetStatus.Superseded,
            scenarioStage
        )
    };
};

export const convertHasuraClusterToCluster = (
    hasuraData: GetAllClustersQuery | undefined
): Cluster[] => {
    if (!hasuraData) {
        return [];
    }
    const clusters = hasuraData.in_queue_clusters
        .map((hasuraCluster) => {
            const region = castRegion(hasuraCluster.region);
            const studyCycle = castStudyCycle(hasuraCluster.study_cycle);
            const studyGroup = castStudyGroup(hasuraCluster.study_group);
            const studyPhase = castStudyPhase(hasuraCluster.study_phase);
            const status = castToClusterStatus(hasuraCluster.status);
            if (
                !studyPhase ||
                !status ||
                !region ||
                !studyCycle ||
                !studyGroup
            ) {
                console.error(
                    `Invalid cluster: ${JSON.stringify(hasuraCluster)}`
                );
                return null;
            }
            let modelSets: ReadonlyArray<ClusterModelSet> = [];
            if (hasuraCluster.in_queue_cluster_model_sets) {
                modelSets = hasuraCluster.in_queue_cluster_model_sets.map(
                    (hasuraClusterModelSet) => {
                        return {
                            clusterModelSetId:
                                hasuraClusterModelSet.id as string,
                            modelSetId:
                                hasuraClusterModelSet.iq_model_set_id as string,
                            name: hasuraClusterModelSet.name as string,
                            status: hasuraClusterModelSet.status,
                            scenarioStage:
                                hasuraClusterModelSet.scenario_stage as ScenarioStage,
                            createdAt: new Date(
                                hasuraClusterModelSet.created_at
                            )
                        };
                    }
                );
            }

            const previewModelSets = buildModelSets(modelSets, "PREVIEW");
            const decisionPointModelSets = buildModelSets(
                modelSets,
                "DECISION_POINT"
            );

            return {
                id: hasuraCluster.id as string,
                region,
                studyCycle,
                studyGroup,
                studyPhase,
                status,
                slug: hasuraCluster.slug as ClusterSlug,
                previewModelSets,
                decisionPointModelSets,
                allModelSets: modelSets
            };
        })
        .filter((cluster): cluster is Cluster => cluster !== null);
    return clusters;
};

export const getClusterLabel = (
    cluster: Cluster | LegacyClusterInfo
): string => {
    return `${cluster.region} ${getCycleAndGroupLabel(
        cluster.region,
        cluster.studyCycle,
        cluster.studyGroup
    )} ${getPhaseLabel(cluster.studyPhase)}`;
};

export const getCycleAndGroupLabel = (
    region: RegionType,
    cycle: StudyCycle,
    group: StudyGroup
): string => {
    const cycleLabel = getStudyCycleLabel(region, cycle);
    const groupLabel = getStudyGroupLabel(region, group);
    return cycleLabel + (groupLabel ? ` ${groupLabel}` : "");
};

export const getStudyGroupLabel = (
    region: RegionType,
    group: StudyGroup
): string | null => {
    if (region === RegionType.MISO && (group === "Atc" || group === "Itc")) {
        return `East (${group.toUpperCase()})`;
    } else if (group === StudyGroup.DEFAULT_GROUP) {
        return null;
    }
    return group;
};

export const getStudyCycleLabel = (
    region: RegionType,
    studyCycle: StudyCycle
): string => {
    if (region === RegionType.MISO) {
        return studyCycle.replace("DPP-", "").replace("-Cycle", "");
    } else if (region === RegionType.PJM) {
        return studyCycle.replace("Transition Cycle ", "TC");
    } else if (region === RegionType.SPP) {
        return studyCycle.replace("DISIS_", "");
    } else if (region == RegionType.NYISO) {
        const year = studyCycle.replace("CY_", "");
        // After 2023, NYISO uses "Cluster" instead of "Class Year".
        if (year === "2023") {
            return studyCycle.replace("CY_", "Class Year ");
        }
        return studyCycle.replace("CY_", "Cluster ");
    }
    return studyCycle;
};

const PHASE_LABELS: Record<StudyPhase, string> = {
    resubmission: "Resubmission",
    one_restudy: "Phase 1 – Restudy",
    two_restudy: "Phase 2 – Restudy",
    three_restudy: "Phase 3 – Restudy",
    pre_screen: "Pre-screen",
    one: "Phase 1",
    two: "Phase 2",
    three: "Phase 3"
};
const CONCISE_PHASE_LABELS: Record<StudyPhase, string> = {
    resubmission: "resubmission",
    one_restudy: "restudy1",
    two_restudy: "restudy2",
    three_restudy: "restudy3",
    pre_screen: "prescreen",
    one: "ph1",
    two: "ph2",
    three: "ph3"
};

export const getPhaseLabel = (
    studyPhase: StudyPhase,
    concise = false
): string => {
    if (concise) {
        return CONCISE_PHASE_LABELS[studyPhase];
    } else {
        return PHASE_LABELS[studyPhase];
    }
};

/**
 * START: remove this when we update schema to point to the new cluster type.
 * linear.app/nira-energy/issue/NIRA-4406/reference-in-queue-clusters-in-other-schema
 */

interface InQueueClusterLegacyUnverified {
    region: RegionType | string;
    studyCycle: StudyCycle | string;
    studyGroup: StudyGroup | string;
    studyPhase: StudyPhase | string;
}

export interface LegacyClusterInfo {
    region: RegionType;
    studyCycle: StudyCycle;
    studyGroup: StudyGroup;
    studyPhase: StudyPhase;
}

export const isValidLegacyClusterInfo = (
    clusterInfo: InQueueClusterLegacyUnverified
): clusterInfo is LegacyClusterInfo => {
    const { region, studyCycle, studyGroup, studyPhase } = clusterInfo;
    const validatedRegion = castRegion(region);
    const validatedStudyCycle = castStudyCycle(studyCycle);
    const validatedStudyGroup = castStudyGroup(studyGroup);
    const validatedStudyPhase = castStudyPhase(studyPhase);
    return !!(
        validatedRegion &&
        validatedStudyCycle &&
        validatedStudyGroup &&
        validatedStudyPhase
    );
};

const convertToLegacyClusterInfo = (
    cluster: Cluster | LegacyClusterInfo
): LegacyClusterInfo => {
    return {
        region: cluster.region,
        studyCycle: cluster.studyCycle,
        studyGroup: cluster.studyGroup,
        studyPhase: cluster.studyPhase
    };
};

export const castToLegacyClusterInfo = (
    cluster: InQueueClusterLegacyUnverified
): LegacyClusterInfo | null => {
    if (isValidLegacyClusterInfo(cluster)) {
        return convertToLegacyClusterInfo(cluster);
    }
    return null;
};

export const isSameCluster = (
    cluster1: Cluster | LegacyClusterInfo,
    cluster2: Cluster | LegacyClusterInfo,
    options: {
        ignoreStudyGroup: boolean;
    } = {
        ignoreStudyGroup: false
    }
) => {
    const cluster1Legacy = convertToLegacyClusterInfo(cluster1);
    const cluster2Legacy = convertToLegacyClusterInfo(cluster2);
    const isStudyGroupSame =
        options.ignoreStudyGroup ||
        cluster1Legacy.studyGroup === cluster2Legacy.studyGroup;
    const isSame =
        cluster1Legacy.region === cluster2Legacy.region &&
        cluster1Legacy.studyCycle === cluster2Legacy.studyCycle &&
        isStudyGroupSame &&
        cluster1Legacy.studyPhase === cluster2Legacy.studyPhase;
    return isSame;
};

/**
 * END: remove this when we update schema to point to the new cluster type.
 * linear.app/nira-energy/issue/NIRA-4406/reference-in-queue-clusters-in-other-schema
 */

/**
 * Returns true if an item (project or scenario) belongs to the given cluster.
 * When the cluster uses the default_group study group, items from any study
 * group are included - this supports clusters that aggregate queue projects
 * from multiple MISO study groups.
 */
export const shouldShowInCluster = (
    itemCluster: Cluster | LegacyClusterInfo,
    cluster: Cluster | LegacyClusterInfo
): boolean =>
    isSameCluster(itemCluster, cluster, {
        ignoreStudyGroup:
            convertToLegacyClusterInfo(cluster).studyGroup ===
            StudyGroup.DEFAULT_GROUP
    });
